import {
	type ProjectType,
	SERVICE_DESK_PROJECT,
	PRODUCT_DISCOVERY_PROJECT,
	SOFTWARE_PROJECT,
} from '@atlassian/jira-common-constants';
import type { MessageDescriptorV2 } from '@atlassian/jira-intl';
import {
	PREMIUM_EDITION,
	type ApplicationEdition,
	STANDARD_EDITION,
} from '@atlassian/jira-shared-types';
import {
	LEARN_MORE_ABOUT_BENEFITS_JPD_URL,
	LEARN_MORE_ABOUT_BENEFITS_JSM_URL,
	LEARN_MORE_ABOUT_BENEFITS_JSW_URL,
	REFERRER_JPD,
	REFERRER_JSM,
	REFERRER_JSW,
} from './constants';
import messages from './messages';

export type ContentModel = {
	aboveFreeUserLimitTitle: MessageDescriptorV2;
	belowFreeUserLimitTitle: MessageDescriptorV2;
	adminAboveFreeUserLimitInstruction: MessageDescriptorV2;
	adminBelowFreeUserLimitInstruction: MessageDescriptorV2;
	billingAdminBenefitTitle: MessageDescriptorV2;
	nonBillingAdminBenefitTitle: MessageDescriptorV2;
	contactBillingAdmin: MessageDescriptorV2;
	benefits: MessageDescriptorV2[];
	learnMoreAboutBenefitsUrl: string;
	learnMoreUrlText: MessageDescriptorV2;
	referrer: string;
	showAtlassianStart?: boolean;
};

export const getContentModel = (
	projectType: ProjectType,
	edition?: ApplicationEdition,
): ContentModel => {
	switch (projectType) {
		case SERVICE_DESK_PROJECT:
			return {
				aboveFreeUserLimitTitle: messages.aboveFreeUserLimitTitleJsm,
				belowFreeUserLimitTitle: messages.belowFreeUserLimitTitle,
				adminAboveFreeUserLimitInstruction: messages.adminAboveFreeUserLimitInstruction,
				adminBelowFreeUserLimitInstruction: messages.adminBelowFreeUserLimitInstruction,
				billingAdminBenefitTitle: messages.billingAdminBenefitTitle,
				nonBillingAdminBenefitTitle: messages.nonBillingAdminBenefitTitle,
				benefits: [
					messages.accessAllAgents,
					messages.businessHourSupport,
					messages.unlimitedEmailNotification,
				],
				contactBillingAdmin: messages.contactBillingAdmin,
				learnMoreAboutBenefitsUrl: LEARN_MORE_ABOUT_BENEFITS_JSM_URL,
				learnMoreUrlText: messages.learnMoreUrlText,
				referrer: REFERRER_JSM,
			};

		case PRODUCT_DISCOVERY_PROJECT:
			return {
				aboveFreeUserLimitTitle: messages.aboveFreeUserLimitTitleJpd,
				belowFreeUserLimitTitle: messages.belowFreeUserLimitTitle,
				adminAboveFreeUserLimitInstruction: messages.adminAboveFreeUserLimitInstruction,
				adminBelowFreeUserLimitInstruction: messages.adminBelowFreeUserLimitInstruction,
				billingAdminBenefitTitle: messages.billingAdminBenefitTitle,
				nonBillingAdminBenefitTitle: messages.nonBillingAdminBenefitTitle,
				benefits: [
					messages.accessAllCreators,
					messages.businessHourSupport,
					messages.dataResidency,
				],
				contactBillingAdmin: messages.contactBillingAdmin,
				learnMoreAboutBenefitsUrl: LEARN_MORE_ABOUT_BENEFITS_JPD_URL,
				learnMoreUrlText: messages.learnMoreUrlText,
				referrer: REFERRER_JPD,
			};

		case SOFTWARE_PROJECT: {
			switch (edition) {
				case PREMIUM_EDITION:
					return {
						aboveFreeUserLimitTitle: messages.aboveFreeUserLimitTitleJswPremium,
						belowFreeUserLimitTitle: messages.belowFreeUserLimitTitlePremium,
						adminAboveFreeUserLimitInstruction: messages.adminAboveFreeUserLimitInstructionPremium,
						adminBelowFreeUserLimitInstruction: messages.adminBelowFreeUserLimitInstructionPremium,
						billingAdminBenefitTitle: messages.billingAdminBenefitTitlePremium,
						nonBillingAdminBenefitTitle: messages.nonBillingAdminBenefitTitlePremium,
						benefits: [
							messages.crossTeamTimelines,
							messages.releaseTracks,
							messages.atlassianIntelligence,
						],
						contactBillingAdmin: messages.contactBillingAdminPremium,
						learnMoreAboutBenefitsUrl: LEARN_MORE_ABOUT_BENEFITS_JSW_URL,
						learnMoreUrlText: messages.premiumLearnMoreUrlText,
						referrer: REFERRER_JSW,
						showAtlassianStart: true,
					};

				case STANDARD_EDITION:
				default:
					return {
						aboveFreeUserLimitTitle: messages.aboveFreeUserLimitTitleJswSpork,
						belowFreeUserLimitTitle: messages.belowFreeUserLimitTitle,
						adminAboveFreeUserLimitInstruction: messages.adminAboveFreeUserLimitInstruction,
						adminBelowFreeUserLimitInstruction: messages.adminBelowFreeUserLimitInstruction,
						billingAdminBenefitTitle: messages.billingAdminBenefitTitle,
						nonBillingAdminBenefitTitle: messages.nonBillingAdminBenefitTitle,
						benefits: [
							messages.accessAllUsers,
							messages.businessHourSupport,
							messages.userRolesAndPermissions,
						],
						contactBillingAdmin: messages.contactBillingAdmin,
						learnMoreAboutBenefitsUrl: LEARN_MORE_ABOUT_BENEFITS_JSW_URL,
						learnMoreUrlText: messages.learnMoreUrlText,
						referrer: REFERRER_JSW,
					};
			}
		}

		default:
			throw new Error(`Unexpected projectType ${projectType}`);
	}
};

export const lastSevenPredunningDaysLeft = (predunningDaysLeft: number | undefined) =>
	predunningDaysLeft !== undefined && predunningDaysLeft >= 0 && predunningDaysLeft < 7;
